// Full Calendar Plugins
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import fiLocale from '@fullcalendar/core/locales/fi'
import enLocale from '@fullcalendar/core/locales/en-gb'
import deLocale from '@fullcalendar/core/locales/de'

// eslint-disable-next-line object-curly-newline
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'

export default function userCalendar() {
  // Use toast
  const toast = useToast()
  // ------------------------------------------------
  // refCalendar
  // ------------------------------------------------
  const refCalendar = ref(null)

  // ------------------------------------------------
  // calendarApi
  // ------------------------------------------------
  let calendarApi = null
  onMounted(() => {
    calendarApi = refCalendar.value.getApi()
  })

  // ------------------------------------------------
  // calendars
  // ------------------------------------------------
  const calendarsColor = {
    Booked: 'warning',
    Verified: 'done',
    Confirmed: 'success',
    Expired: 'danger',
    Open: 'info',
    Unconfirmed: 'secondary',
    Draft: 'purple',
  }

  // ------------------------------------------------
  // event
  // ------------------------------------------------
  const blankEvent = {
    title: '',
    start: '',
    end: '',
    allDay: false,
    url: '',
    reserved: false,
    extendedProps: {
      calendar: '',
      guests: [],
      location: '',
      description: '',
      kilometrikorvaus: 0,
      workDayType: '',
      updates: [],
      boosted: false,
      boostedOptions: {},
    },
  }
  const event = ref(JSON.parse(JSON.stringify(blankEvent)))
  const clearEventData = () => {
    event.value = JSON.parse(JSON.stringify(blankEvent))
  }

  // ------------------------------------------------
  // grabEventDataFromEventApi
  // ? It will return just event data from fullCalendar's EventApi which is not required for event mutations and other tasks
  // ! You need to update below function as per your extendedProps
  // ------------------------------------------------
  const grabEventDataFromEventApi = eventApi => {
    const {
      id,
      title,
      start,
      startStr,
      endStr,
      end,
      status,
      // eslint-disable-next-line object-curly-newline
      extendedProps: {
        calendar,
        location,
        gigId,
        areaId,
        accountId,
        isDraft,
        assignedTo, 
        requirements,
        reserved, 
        kilometrikorvaus,
        workDayType, 
        updates, 
        boosted, 
        boostedOptions,
        contractId,
      },
      allDay,
    } = eventApi

    return {
      id,
      title,
      start,
      startStr,
      endStr,
      end,
      status,
      extendedProps: {
        calendar,
        isDraft,
        location,
        gigId,
        areaId,
        accountId,
        assignedTo,
        requirements,
        reserved,
        kilometrikorvaus,
        workDayType,
        updates,
        boosted,
        boostedOptions,
        contractId,
      },
      allDay,
    }
  }

  const isEventHandlerSidebarActive = ref(false)
  const areaId = ref(null)



  // ------------------------------------------------
  // refetchEvents
  // ------------------------------------------------
  const refetchEvents = () => {
    calendarApi.refetchEvents()
  }

  // ------------------------------------------------
  // selectedCalendars
  // ------------------------------------------------
  const selectedCalendars = computed(() => store.state.appCalendar.selectedCalendars)
  const selectedAccount = computed(() => store.state.appAccount.selectedAccount)
  const currentUser = computed(() => store.state.appAuth.authUser)

  watch(selectedCalendars, () => {
    refetchEvents()
  })

  watch(selectedAccount, () => {
    refetchEvents()
  })

  watch(areaId, () => {
    refetchEvents()
  })

  const searchLocation = ({ text, callback }) => {
    if (text) {
      store.dispatch('appCalendar/fetchLocation', { text, accountId: selectedAccount.value.value.id }).then(data => {
        callback(data)
      })
    }
  }

  // --------------------------------------------------------------------------------------------------
  // AXIOS: fetchEvents
  // * This will be called by fullCalendar to fetch events. Also this can be used to refetch events.
  // --------------------------------------------------------------------------------------------------
  const fetchEvents = (info, successCallback) => {
    // If there's no info => Don't make useless API call
    if (!info) return
    // Fetch Events from API endpoint
    const accountId = selectedAccount.value.value.id
    const loggedUser = currentUser.value
    const onlyAreas = loggedUser.role === 'customer' && loggedUser.areas ? loggedUser.areas : null
    // const formatSelectedCalendar = selectedCalendars.value.map(item => item.value)
    store
      .dispatch('appCalendar/fetchEvents', {
        info,
        calendars: selectedCalendars.value,
        accountId,
        onlyAreas,
        areaId: areaId.value,
      })
      .then(response => {
        successCallback(response)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe vuorojen hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // ------------------------------------------------------------------------
  // calendarOptions
  // * This isn't considered in UI because this is the core of calendar app
  // ------------------------------------------------------------------------
  const calendarOptions = ref({
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      start: 'sidebarToggle, prev,next, title',
      end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
    },
    locales: [fiLocale, enLocale, deLocale],
    locale: i18n.locale,
    events: fetchEvents,

    /*
      Enable dragging and resizing event
      ? Docs: https://fullcalendar.io/docs/editable
    */
    editable: false,

    /*
      Enable resizing event from start
      ? Docs: https://fullcalendar.io/docs/eventResizableFromStart
    */
    eventResizableFromStart: false,

    /*
      Automatically scroll the scroll-containers during event drag-and-drop and date selecting
      ? Docs: https://fullcalendar.io/docs/dragScroll
    */
    dragScroll: true,

    /*
      Max number of events within a given day
      ? Docs: https://fullcalendar.io/docs/dayMaxEvents
    */
    dayMaxEvents: 100,

    /*
      Determines if day names and week names are clickable
      ? Docs: https://fullcalendar.io/docs/navLinks
    */
    navLinks: false,

    eventClassNames({ event: calendarEvent }) {
      // eslint-disable-next-line no-underscore-dangle
      const colorName = calendarsColor[calendarEvent._def.extendedProps.calendar]

      return [
        // Background Color
        `bg-light-${colorName}`,
      ]
    },
    eventClick({ event: clickedEvent }) {
      // * Only grab required field otherwise it goes in infinity loop
      // ! Always grab all fields rendered by form (even if it get `undefined`) otherwise due to Vue3/Composition API you might get: "object is not extensible"
      event.value = grabEventDataFromEventApi(clickedEvent)

      // eslint-disable-next-line no-use-before-define
      isEventHandlerSidebarActive.value = true
    },

    customButtons: {
      sidebarToggle: {
        // --- This dummy text actual icon rendering is handled using SCSS ----- //
        text: 'sidebar',
        click() {
          // eslint-disable-next-line no-use-before-define
          isCalendarOverlaySidebarActive.value = !isCalendarOverlaySidebarActive.value
        },
      },
    },

    /* dateClick(info) {
      // event.value = JSON.parse(JSON.stringify(Object.assign(event.value, { start: info.date })))
      // eslint-disable-next-line no-use-before-define
       isEventHandlerSidebarActive.value = true
     }, */

    // Get direction from app state (store)
    direction: computed(() => (store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr')),
    rerenderDelay: 350,
  })

  // ------------------------------------------------------------------------

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const isCalendarOverlaySidebarActive = ref(false)


  return {
    refCalendar,
    isCalendarOverlaySidebarActive,
    calendarOptions,
    event,
    clearEventData,
    refetchEvents,
    fetchEvents,
    // ----- UI ----- //
    isEventHandlerSidebarActive,
    areaId,
    searchLocation,
  }
}
